import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import { getConfig } from '../config/config-helper';
import type {
  ISolutionDeploymentListItem,
  ISolutionDeploymentDetail,
  ICreateSolutionDeploymentRequest,
  ISolutionDeploymentSuccessResponse,
  ISolutionDeploymentErrorResponse,
} from '../interfaces/ISolutionDeployment';
import type { RootState } from '../store';

interface ISolutionDeploymentsState {
  solutonDeployments: ISolutionDeploymentListItem[];
  deploymentDetails: ISolutionDeploymentDetail[];
  requestStatus: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: string | null;
}

const initialState: ISolutionDeploymentsState = {
  solutonDeployments: [],
  deploymentDetails: [],
  requestStatus: 'idle',
  error: null,
};

// Fetch all solution solutonDeployments for the current user
export const fetchSolutionDeployments = createAsyncThunk(
  'solutionDeployments/fetchAll',
  async (token: string, { rejectWithValue }) => {
    try {
      const { dataopsAdminAppApiEndpoint } = getConfig();
      const response = await axios.get<ISolutionDeploymentListItem[]>(
        `${dataopsAdminAppApiEndpoint}/solution_demos/deployments/`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      return response.data;
    } catch (error: any) {
      if (error.response?.data) {
        return rejectWithValue(error.response.data as ISolutionDeploymentErrorResponse);
      }
      return rejectWithValue({ message: 'Failed to fetch solutonDeployments', message_code: 'ERR_NETWORK' });
    }
  },
);

// Fetch a single solution deployment by ID
export const fetchSolutionDeployment = createAsyncThunk(
  'solutionDeployments/fetchOne',
  async ({ token, deploymentId }: { token: string; deploymentId: number }, { rejectWithValue }) => {
    try {
      const { dataopsAdminAppApiEndpoint } = getConfig();
      const response = await axios.get<ISolutionDeploymentDetail>(
        `${dataopsAdminAppApiEndpoint}/solution_demos/deployments/${deploymentId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      return response.data;
    } catch (error: any) {
      if (error.response?.data) {
        return rejectWithValue(error.response.data as ISolutionDeploymentErrorResponse);
      }
      return rejectWithValue({ message: 'Failed to fetch deployment', message_code: 'ERR_NETWORK' });
    }
  },
);

// Create a new solution deployment
export const createSolutionDeployment = createAsyncThunk(
  'solutionDeployments/create',
  async ({ token, payload }: { token: string; payload: ICreateSolutionDeploymentRequest }, { rejectWithValue }) => {
    try {
      const { dataopsAdminAppApiEndpoint } = getConfig();
      const response = await axios.post<ISolutionDeploymentSuccessResponse>(
        `${dataopsAdminAppApiEndpoint}/solution_demos/deployments/`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        },
      );
      return response.data;
    } catch (error: any) {
      if (error.response?.data) {
        return rejectWithValue(error.response.data as ISolutionDeploymentErrorResponse);
      }
      return rejectWithValue({ message: 'Failed to create deployment', message_code: 'ERR_NETWORK' });
    }
  },
);

// Update (trigger new pipeline run) for an existing solution deployment
export const updateSolutionDeployment = createAsyncThunk(
  'solutionDeployments/update',
  async ({ token, deploymentId }: { token: string; deploymentId: number }, { rejectWithValue }) => {
    try {
      const { dataopsAdminAppApiEndpoint } = getConfig();
      const response = await axios.post<ISolutionDeploymentSuccessResponse>(
        `${dataopsAdminAppApiEndpoint}/solution_demos/deployments/${deploymentId}/update`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      return response.data;
    } catch (error: any) {
      if (error.response?.data) {
        return rejectWithValue(error.response.data as ISolutionDeploymentErrorResponse);
      }
      return rejectWithValue({ message: 'Failed to update deployment', message_code: 'ERR_NETWORK' });
    }
  },
);

// Mutate a single variable in the extra_env_vars of a solution deployment
export const mutateSolutionDeploymentVariable = createAsyncThunk(
  'solutionDeployments/mutateVariable',
  async (
    {
      token,
      deployment,
      varKey,
      varValue,
    }: {
      token: string;
      deployment: ISolutionDeploymentDetail;
      varKey: string;
      varValue: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const { dataopsAdminAppApiEndpoint } = getConfig();
      // Create updated extra_env_vars with the new value
      const updatedExtraVars = {
        ...deployment.extra_env_vars,
        [varKey]: varValue,
      };

      const response = await axios.put<ISolutionDeploymentSuccessResponse>(
        `${dataopsAdminAppApiEndpoint}/solution_demos/deployments/${deployment.id}/extra-vars`,
        { extra_env_vars: updatedExtraVars },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        },
      );
      return { deploymentId: deployment.id, varKey, varValue, response: response.data };
    } catch (error: any) {
      if (error.response?.data) {
        return rejectWithValue(error.response.data as ISolutionDeploymentErrorResponse);
      }
      return rejectWithValue({ message: 'Failed to update variable', message_code: 'ERR_NETWORK' });
    }
  },
);

const solutionDeploymentsSlice = createSlice({
  name: 'solutionDeployments',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch all solutonDeployments
      .addCase(fetchSolutionDeployments.pending, (state) => {
        state.requestStatus = 'loading';
        state.error = null;
      })
      .addCase(fetchSolutionDeployments.fulfilled, (state, action) => {
        state.requestStatus = 'succeeded';
        state.solutonDeployments = action.payload;
      })
      .addCase(fetchSolutionDeployments.rejected, (state, action) => {
        state.requestStatus = 'failed';
        state.error =
          (action.payload as ISolutionDeploymentErrorResponse)?.message || 'Failed to fetch solutonDeployments';
      })
      // Fetch single deployment
      .addCase(fetchSolutionDeployment.pending, (state) => {
        state.requestStatus = 'loading';
        state.error = null;
      })
      .addCase(fetchSolutionDeployment.fulfilled, (state, action) => {
        state.requestStatus = 'succeeded';
        // Store or update the deployment in the array
        const index = state.deploymentDetails.findIndex((d) => d.id === action.payload.id);
        if (index >= 0) {
          state.deploymentDetails[index] = action.payload;
        } else {
          state.deploymentDetails.push(action.payload);
        }
      })
      .addCase(fetchSolutionDeployment.rejected, (state, action) => {
        state.requestStatus = 'failed';
        state.error = (action.payload as ISolutionDeploymentErrorResponse)?.message || 'Failed to fetch deployment';
      })
      // Create deployment
      .addCase(createSolutionDeployment.pending, (state) => {
        state.requestStatus = 'loading';
        state.error = null;
      })
      .addCase(createSolutionDeployment.fulfilled, (state) => {
        state.requestStatus = 'succeeded';
      })
      .addCase(createSolutionDeployment.rejected, (state, action) => {
        state.requestStatus = 'failed';
        state.error = (action.payload as ISolutionDeploymentErrorResponse)?.message || 'Failed to create deployment';
      })
      // Update deployment
      .addCase(updateSolutionDeployment.pending, (state) => {
        state.requestStatus = 'loading';
        state.error = null;
      })
      .addCase(updateSolutionDeployment.fulfilled, (state) => {
        state.requestStatus = 'succeeded';
      })
      .addCase(updateSolutionDeployment.rejected, (state, action) => {
        state.requestStatus = 'failed';
        state.error = (action.payload as ISolutionDeploymentErrorResponse)?.message || 'Failed to update deployment';
      })
      // Mutate deployment variable
      .addCase(mutateSolutionDeploymentVariable.fulfilled, (state, action) => {
        // Update the deployment details in state with the new variable value
        const { deploymentId, varKey, varValue } = action.payload;
        const deployment = state.deploymentDetails.find((d) => d.id === deploymentId);
        if (deployment) {
          deployment.extra_env_vars = {
            ...deployment.extra_env_vars,
            [varKey]: varValue,
          };
        }
      })
      .addCase(mutateSolutionDeploymentVariable.rejected, (state, action) => {
        state.error =
          (action.payload as ISolutionDeploymentErrorResponse)?.message || 'Failed to update deployment variable';
      });
  },
});

export const { clearError } = solutionDeploymentsSlice.actions;

// Selectors
export const selectSolutionDeployments = (state: RootState) => state.solutionDeployments.solutonDeployments;
export const selectSolutionDeployment = (state: RootState, deploymentId: number | undefined) =>
  state.solutionDeployments.deploymentDetails.find((d) => d.id === deploymentId);
export const selectSolutionDeploymentsStatus = (state: RootState) => state.solutionDeployments.requestStatus;
export const selectSolutionDeploymentsError = (state: RootState) => state.solutionDeployments.error;

export default solutionDeploymentsSlice.reducer;
